/**
 * Hooks + types for the AnswerFeedback API.
 *
 * Two feedback shapes, both addressed at /api/documents/questions/{id}/feedback/
 * and distinguished by `feedback_target`:
 *
 *   - "value": user judging the extracted value itself (right/wrong/unsure)
 *   - "current_selection": user disagreeing with which AnswerGroup was
 *     auto-selected as the current value
 *
 * The list endpoint returns rows for any AnswerGroup of the question, and is
 * filterable by `answer_group` and `feedback_target`.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient, isApiError, type ApiResponse } from "./_client";

/** Thrown by `markFeedback` on a non-2xx response. `apiClient`'s interceptor
 *  resolves HTTP errors into an envelope ({error, errorStatus, formErrors})
 *  rather than throwing, so `markFeedback` detects that and re-surfaces it as
 *  a throw — callers rely on try/catch (and on `status === 409` to handle the
 *  idempotency-conflict case distinctly). */
export class FeedbackApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "FeedbackApiError";
    this.status = status;
  }
}

export type FeedbackTarget = "value" | "current_selection";
export type FeedbackStatus = "correct" | "incorrect" | "unsure";

/** Typed feedback taxonomy. Mirrors `FeedbackCategory` on the backend.
 *  Drives Phase 3 clustering into actionable engineering tickets and
 *  unlocks "recommend better questions" via the question-level family.
 *  Family E carries affirmative signal — what's working — paired with
 *  status=correct. */
export type FeedbackCategory =
  // value-level (negative): extraction misread / mis-sourced
  | "extraction_wrong_value"
  | "wrong_doc_picked"
  | "wrong_clause_picked"
  | "outdated_doc"
  // question-level (negative): question or prompt is the bug
  | "question_ambiguous"
  | "prompt_too_narrow"
  | "prompt_too_broad"
  | "question_not_applicable"
  // context / data
  | "missing_context"
  | "not_in_documents"
  // selection-level (relevant for current_selection target)
  | "auto_selection_wrong"
  | "superseded_by_amendment"
  // affirmative: what's working (paired with status=correct)
  | "question_appropriate"
  | "extraction_consistent"
  | "other";

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  extraction_wrong_value: "Extraction got the value wrong",
  wrong_doc_picked: "Wrong document",
  wrong_clause_picked: "Wrong clause / section",
  outdated_doc: "Outdated document version",
  question_ambiguous: "Question is ambiguous",
  prompt_too_narrow: "Extraction prompt too narrow",
  prompt_too_broad: "Extraction prompt too broad",
  question_not_applicable: "Question doesn't apply here",
  missing_context: "Missing context",
  not_in_documents: "Not in the documents",
  auto_selection_wrong: "Auto-selector picked wrong",
  superseded_by_amendment: "Superseded by amendment",
  question_appropriate: "Question is well-formed for this work",
  extraction_consistent: "Extraction reads this doc type correctly",
  other: "Other (see comment)",
};

/** Categories appropriate for negative value-target feedback (incorrect / unsure). */
export const FEEDBACK_CATEGORIES_FOR_VALUE_NEGATIVE: FeedbackCategory[] = [
  "extraction_wrong_value",
  "wrong_doc_picked",
  "wrong_clause_picked",
  "outdated_doc",
  "question_ambiguous",
  "prompt_too_narrow",
  "prompt_too_broad",
  "question_not_applicable",
  "missing_context",
  "not_in_documents",
  "other",
];

/** Categories appropriate for affirmative value-target feedback (correct).
 *  Capturing what works is the same training signal as capturing what
 *  doesn't — without it we'd only ever hear about failures. */
export const FEEDBACK_CATEGORIES_FOR_VALUE_AFFIRMATIVE: FeedbackCategory[] = [
  "question_appropriate",
  "extraction_consistent",
  "other",
];

export const FEEDBACK_CATEGORIES_FOR_CURRENT_SELECTION: FeedbackCategory[] = [
  "auto_selection_wrong",
  "superseded_by_amendment",
  "outdated_doc",
  "missing_context",
  "other",
];

/** Resolves the relevant category subset based on feedback status.
 *  Affirmative when correct; negative otherwise. */
export function categoriesFor(
  status: FeedbackStatus | null,
): FeedbackCategory[] {
  if (status === "correct") return FEEDBACK_CATEGORIES_FOR_VALUE_AFFIRMATIVE;
  return FEEDBACK_CATEGORIES_FOR_VALUE_NEGATIVE;
}

export interface AnswerFeedback {
  id: string;
  feedback_target: FeedbackTarget;
  answer_group: string | null;
  answer: string | null;
  status: FeedbackStatus | null;
  auto_selected_answer_group: string | null;
  user_selected_answer_group: string | null;
  comment: string;
  agent_summary: string;
  category: FeedbackCategory | null;
  report_context: string | null;
  report_context_analysis: string | null;
  report_context_title: string;
  highlight: string | null;
  excluded_from_reformulation_at: string | null;
  excluded_from_reformulation_by: string | null;
  reformulation_exclusion_reason: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_name: string;
}

/** Idempotency key shared by both feedback shapes. Stamped on every
 *  outbound POST so a retried double-submit (network blip, double-click,
 *  retry-on-error) returns the existing row instead of inserting a new
 *  one. The BE keys uniqueness on (org, user, client_event_id) and
 *  fingerprints the operation intent — same key + same intent → same
 *  row; same key + different intent → 409 Conflict. */
interface WithIdempotency {
  client_event_id?: string;
}

export interface MarkValueFeedbackRequest extends WithIdempotency {
  feedback_target: "value";
  answer_group: string;
  answer?: string | null;
  status: FeedbackStatus;
  comment?: string;
  agent_summary?: string;
  category?: FeedbackCategory | null;
  report_context?: string | null;
  highlight?: string | null;
}

export interface MarkCurrentSelectionFeedbackRequest extends WithIdempotency {
  feedback_target: "current_selection";
  auto_selected_answer_group: string;
  user_selected_answer_group: string;
  comment?: string;
  agent_summary?: string;
  category?: FeedbackCategory | null;
  report_context?: string | null;
}

export type MarkFeedbackRequest =
  | MarkValueFeedbackRequest
  | MarkCurrentSelectionFeedbackRequest;

export const answerFeedbackKeys = {
  all: ["answer-feedback"] as const,
  forQuestion: (questionId: string) =>
    [...answerFeedbackKeys.all, "question", questionId] as const,
  forQuestionFiltered: (
    questionId: string,
    filters: { answer_group?: string; feedback_target?: FeedbackTarget } = {},
  ) =>
    [
      ...answerFeedbackKeys.forQuestion(questionId),
      filters.answer_group ?? null,
      filters.feedback_target ?? null,
    ] as const,
};

/** Generate an idempotency key. Prefers `crypto.randomUUID()`; falls back
 *  to a non-cryptographic random hex string on non-secure-context origins
 *  (HTTP dev hosts that aren't localhost), where `crypto.randomUUID()`
 *  is unavailable and would throw. The key only needs to be unique within
 *  (org, user) — full crypto strength isn't required. */
export function newClientEventId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      // Falls through to the random-hex path below — Safari throws on
      // insecure origins instead of leaving randomUUID undefined.
    }
  }
  return (
    Math.random().toString(36).slice(2) +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2)
  );
}

/** POST a feedback row. Caller is responsible for invalidating the relevant
 *  list query after a successful response.
 *
 *  An idempotency key (`client_event_id`) is auto-stamped if the caller
 *  didn't supply one. Each call by default represents one logical user
 *  action — a retry should resolve to the same row. Callers that
 *  explicitly want to dedupe across retries of the same logical click
 *  should pass their own key (e.g. one stamped at click time rather than
 *  call time). */
export const markFeedback = async (
  questionId: string,
  body: MarkFeedbackRequest,
): Promise<AnswerFeedback> => {
  const response = await apiClient.post<AnswerFeedback>(
    `api/documents/questions/${questionId}/feedback/`,
    { client_event_id: newClientEventId(), ...body },
  );
  // apiClient's interceptor resolves a non-2xx into an error envelope
  // instead of throwing — detect it with the shared isApiError guard and
  // throw, so callers' catch runs (a silent success would leave the UI
  // showing feedback as saved when it wasn't).
  if (isApiError(response)) {
    throw new FeedbackApiError(
      response.error || "Failed to save feedback.",
      response.errorStatus,
    );
  }
  return response.data;
};

/** Retract (soft-delete) a feedback row — the "undo" path when a user
 *  re-clicks their already-selected verdict. 204 on success; throws a
 *  FeedbackApiError on the apiClient error envelope so the caller can
 *  roll back. Caller invalidates / updates the relevant list query. */
export const retractFeedback = async (feedbackId: string): Promise<void> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/feedback/${feedbackId}/`,
  );
  if (isApiError(response)) {
    throw new FeedbackApiError(
      response.error || "Failed to remove feedback.",
      response.errorStatus,
    );
  }
};

/** Query feedback rows attached to any AnswerGroup of the question.
 *  Pass `answerGroupId` to scope to a single group; pass `target` to
 *  scope to a single feedback shape. */
export const useAnswerFeedback = ({
  questionId,
  answerGroupId,
  target,
  mineOnly = false,
  fetchAll = false,
  enabled = true,
}: {
  questionId: string | null | undefined;
  answerGroupId?: string | null;
  target?: FeedbackTarget;
  /** Scope to the current user's own rows server-side. Without this the
   *  list is org-wide and paginated (page 1 of 10); on a popular answer the
   *  caller's own row can fall off-page and the control falsely shows "no
   *  feedback", then silently overwrites it. */
  mineOnly?: boolean;
  /** Page through the entire list instead of just page 1. Aggregation
   *  surfaces (e.g. the Question Improvements tab) compute counts over the
   *  whole feedback history; reading page 1 of the `-created_at`-ordered
   *  list silently truncates to the 10 most-recent rows, so older commented
   *  feedback buried under recent affirmative clicks goes uncounted. */
  fetchAll?: boolean;
  enabled?: boolean;
}): UseQueryResult<AnswerFeedback[]> => {
  return useQuery<AnswerFeedback[]>({
    queryKey: [
      ...answerFeedbackKeys.forQuestionFiltered(questionId ?? "", {
        answer_group: answerGroupId ?? undefined,
        feedback_target: target,
      }),
      mineOnly ? "mine" : "all",
      fetchAll ? "all-pages" : "page-1",
    ],
    queryFn: async () => {
      const baseParams = new URLSearchParams();
      if (answerGroupId) baseParams.set("answer_group", answerGroupId);
      if (target) baseParams.set("feedback_target", target);
      if (mineOnly) baseParams.set("mine", "true");

      const fetchPage = async (page?: number) => {
        const params = new URLSearchParams(baseParams);
        if (page != null) {
          params.set("page", String(page));
          params.set("page_size", "100");
        }
        const qs = params.toString();
        const url =
          `api/documents/questions/${questionId}/feedback/` +
          (qs ? `?${qs}` : "");
        // Backend wraps results in StandardResultsSetPagination's envelope
        // ({count, page_size, next, previous, results}).
        const response = await apiClient.get<{
          results: AnswerFeedback[];
          next: string | null;
        }>(url);
        return response.data ?? { results: [], next: null };
      };

      if (!fetchAll) {
        return (await fetchPage()).results ?? [];
      }

      const all: AnswerFeedback[] = [];
      for (let page = 1; ; page++) {
        const { results, next } = await fetchPage(page);
        all.push(...(results ?? []));
        if (!next) break;
      }
      return all;
    },
    enabled: enabled && !!questionId,
    staleTime: 30 * 1000,
  });
};
